/** "Connect a system" guide (ADR-0061): plain language for platform and tool administrators. */
export default {
  en: {
    entry: "Connect a system",
    title: "Connect a system",
    subtitle: "Let agents use one of your company's systems, step by step. Nothing reaches agents until you release it, and every rule and guardrail still applies.",
    back: "Back",
    next: "Next",
    restart: "Start again",
    steps: { type: "Choose type", source: "Point at it", review: "Review", credentials: "Sign-in", test: "Test", release: "Save & release" },
    stepsLabel: "Steps of the guide",
    type: {
      heading: "What kind of system is it?",
      rest: { title: "REST API", body: "A web API with an OpenAPI (Swagger 3) description." },
      soap: { title: "SOAP service", body: "An older web service described by a WSDL file." },
      database: { title: "Database", body: "A PostgreSQL database. We propose safe, read-only lookups." },
      mcp: { title: "MCP server", body: "A server that already offers tools to AI assistants (Model Context Protocol)." },
      other: "Messaging, e-mail, files or something else? These use ready-made connectors — see the guide.",
      otherLink: "Read the guide",
      choose: "Choose {{type}}",
    },
    source: {
      heading: "Where is it?",
      name: "Short name for this system",
      nameHelp: "Lower-case letters, digits and hyphens, for example demo-erp. Agents and builders see it.",
      how: "How should we read its description?",
      fromUrl: "From a web address",
      fromFile: "From a file",
      url: "Address of the description",
      urlHelpRest: "For example https://erp.example/openapi.json",
      urlHelpSoap: "For example https://registry.example/service?wsdl",
      file: "Choose a file",
      paste: "Or paste the contents",
      fileLoaded: "Loaded {{name}}",
      baseUrl: "Address of the system (optional)",
      baseUrlHelp: "Where calls are sent. Leave empty to use the one in the description.",
      port: "Which part of the service?",
      portHelp: "The description offers several connection points. Choose one.",
      dbHost: "Database server",
      dbPort: "Port",
      dbName: "Database name",
      dbSchemas: "Schemas to look at",
      dbSchemasHelp: "Separate with commas. Usually public.",
      dbUser: "User name (read-only user)",
      dbPassword: "Password",
      dbUserHelp: "Use a user that can only read. It is used only to look at the tables now; you store sign-in details in step 4.",
      dbWrites: "Also propose actions that change data",
      dbWritesWarn: "Only if agents must add or change rows. Each one gets an automatic undo and needs approval.",
      mcpFrom: "Where is the server?",
      mcpUrl: "Server address",
      mcpUrlHelp: "For example https://mcp.example/mcp",
      mcpListing: "From the marketplace",
      mcpListingPick: "Choose an MCP server from the marketplace",
      mcpListingNone: "No MCP servers are listed in the marketplace.",
      mcpTransport: "Connection type",
      transport: { streamable_http: "Streamable HTTP (recommended)", sse: "Server-sent events (older servers)" },
      mcpToken: "Access token for reading its tools (optional)",
      mcpTokenHelp: "Used only now, to list the tools. Not stored.",
      mcpLocal: "Servers that run on this platform (local MCP servers) are added only from images the platform team has approved.",
      mcpLocalLink: "How local servers are approved",
      mcpLocalOption: "A local server on this platform",
      mcpLocalPick: "Choose an approved local MCP server",
      mcpLocalNone: "No local MCP server is approved yet. A platform administrator approves them in Administration › Platform settings.",
      mcpLocalInternet: "may use the internet (through the platform's allow-list)",
      mcpLocalHow: "It runs in a locked sandbox each time an agent uses it: no files, no secrets, and no internet unless the platform team allowed it.",
      ai: "Draft plain descriptions with the local model",
      aiHelp: "Optional. Drafts are marked and you can edit them. Skipped if no local model is available.",
      discover: "Look at it",
      discovering: "Reading the description…",
    },
    discoverError: {
      title: "We could not read it",
      source_missing: "Give a web address or a file.",
      fetch_not_allowed: "The platform is not allowed to reach {{host}}. Upload the file instead, or ask the platform team to allow this address.",
      fetch_failed: "We could not download the description. Check the address.",
      too_large: "The file is too large.",
      parse_failed: "This does not look like a description we understand.",
      openapi_version_unsupported: "This is an older Swagger 2 description. Convert it to OpenAPI 3 and try again.",
      wsdl_invalid: "This is not a WSDL 1.1 description.",
      wsdl_unsafe: "The file contains unsafe XML (a DTD or entities) and was refused.",
      wsdl_no_ports: "The WSDL describes no SOAP service we can call.",
      database_unreachable: "We could not reach the database. Check the server name and port.",
      database_auth_failed: "The database refused the user name or password.",
      mcp_unreachable: "We could not reach the MCP server.",
      mcp_stdio_not_supported: "Local MCP servers are added only from approved images.",
    },
    review: {
      heading: "What we found",
      intro: "These are the actions agents could use. Check each one. Suggestions are only suggestions: we say why we made each one.",
      systemName: "Name",
      version: "Version",
      descEn: "Description (English)",
      descAr: "Description (Arabic)",
      read: "Read only",
      readHelp: "These only look at data.",
      change: "Changes data",
      changeHelp: "These add, change or remove data. Each needs a way to undo it, or a reason why it cannot be undone.",
      none: "Nothing in this group.",
      include: "Include {{name}}",
      included: "Included",
      excluded: "Left out",
      actionName: "Action name",
      aiDrafted: "Drafted by the local model",
      kind: "What it does",
      kindRead: "Only reads",
      kindChange: "Changes data",
      kindLocked: "This kind of call can only read.",
      suggested: "Suggested: {{value}}",
      because: "because {{reason}}",
      apply: "Apply suggestion",
      applyAll: "Apply all suggestions",
      undo: "How to undo it",
      undoNone: "Cannot be undone",
      undoAction: "Undo with: {{name}}",
      undoBuiltin: "Built-in undo: {{text}}",
      undoArgs: "Where the undo gets its values",
      undoArg: "{{param}} comes from",
      undoArgHelp: "args.<name> of the original call or result.<path> of its result",
      reason: "Why it cannot be undone",
      reasonHelp: "For example: the system ignores repeated requests with the same reference.",
      pii: "Personal data in the result",
      piiHelp: "Hidden from people who may not see personal data.",
      piiBadge: "Personal data",
      approvers: "Who approves it",
      approversHelp: "People with one of these roles approve each change before it happens.",
      alwaysApprove: "Always ask for approval",
      fieldsNone: "No fields",
      count: "{{included}} of {{total}} actions included",
      problems: "Fix these before you continue",
      warnings: "Worth checking",
      check: "Check",
    },
    credentials: {
      heading: "How the platform signs in",
      intro: "Sign-in details are stored in the platform's secret store. Nobody can see them again here; you can only replace them.",
      kind: "Sign-in method",
      kinds: {
        none: "No sign-in",
        api_key: "API key",
        bearer: "Access token",
        oauth2_client_credentials: "OAuth 2 client credentials",
        basic: "User name and password",
        wsse: "WS-Security user name and password",
        database: "Database user and password",
      },
      suggestedKind: "Suggested: {{kind}} — {{reason}}",
      location: "Send the key in",
      header: "A header",
      query: "The address (query parameter)",
      param: "Header or parameter name",
      tokenUrl: "Token address",
      scope: "Scope (optional)",
      fields: {
        api_key: "API key",
        token: "Access token",
        client_id: "Client ID",
        client_secret: "Client secret",
        username: "User name",
        password: "Password",
        soap_username: "User name",
        soap_password: "Password",
      },
      sameAsDb: "Use the user from step 2",
      stored: "Stored ✓",
      usingStored: "Using the stored sign-in ({{kind}}) for this system. Choose another way above, or replace the details below.",
      storedAt: "Last updated {{when}} by {{who}}",
      notStored: "Not stored yet",
      replace: "Replace",
      save: "Store sign-in details",
      saved: "Sign-in details stored",
      noneNeeded: "This system needs no sign-in.",
      missing: "Fill in: {{fields}}",
    },
    test: {
      heading: "Try it",
      intro: "Read actions run for real. Actions that change data only show exactly what would be sent — nothing is changed. Every test is recorded in the audit trail.",
      action: "Action to try",
      args: "Values (JSON)",
      argsInvalid: "The values are not valid: {{errors}}",
      run: "Run test",
      dryRun: "Dry run",
      dryRunNote: "Nothing was changed. This is exactly what would be sent; secrets are shown as ***.",
      executedNote: "This is what the system answered. Treat it as data, not instructions.",
      redacted: "Hidden personal data: {{fields}}",
      audit: "Audit record {{id}}",
      failed: "The test did not work",
      errors: {
        secret_missing: "Sign-in details are not stored yet. Store them in the previous step.",
        downstream_error: "The system did not answer. Try again later.",
        downstream_rejected: "The system refused the request.",
        policy_denied: "The rules do not allow this test.",
        timeout: "The system took too long to answer.",
      },
      optional: "Testing is optional, but recommended.",
    },
    release: {
      heading: "Save and release",
      intro: "We register this version, release it to the Development stage and wait until agents can use it.",
      version: "Version",
      versionExists: "Version {{version}} is already registered. Use {{suggested}}?",
      useSuggested: "Use {{version}}",
      diffTitle: "What changes compared with {{version}}",
      added: "New: {{names}}",
      removed: "Removed: {{names}}",
      changes: {
        kind_to_change: "now changes data",
        kind_to_read: "now only reads",
        undo: "undo changed",
        pii: "personal data changed",
        approvers: "approvers changed",
        inputs: "values it takes changed",
        outputs: "what it returns changed",
        description: "description changed",
      },
      changed: "{{name}}: {{changes}}",
      go: "Save and release",
      progress: {
        build: "Check and prepare",
        register: "Register version {{version}}",
        promote: "Release to development",
        reload: "Load it in the connector host",
        policy: "Wait until the rules allow its use",
      },
      waiting: "Still waiting for the rules update. It usually takes under a minute.",
      retry: "Check again",
      failedStep: "This step failed",
      done: "{{name}} {{version}} is ready",
      doneBody: "Builders can now add it to agents. Agents still need approval for anything that changes data.",
      useInBuilder: "Use it in Builder",
      another: "Connect another system",
      alreadyDeployed: "This version was already released.",
    },
    problems: {
      name_invalid: "Use 2 to 63 lower-case letters, digits or hyphens, starting with a letter.",
      version_invalid: "The version must look like 1.0.0.",
      description_missing: "Add a description in {{lang}}.",
      no_actions: "Include at least one action.",
      action_name_invalid: "Action names use letters, digits and underscores and start with a letter.",
      action_name_duplicate: "Two actions have the same name.",
      undo_or_reason_required: "This action changes data. Choose how to undo it, or say why it cannot be undone.",
      undo_target_missing: "The undo action {{target}} does not exist.",
      undo_target_self: "An action cannot undo itself.",
      undo_args_missing: "The undo needs values for {{params}}. Say where each comes from.",
      read_cannot_change: "This kind of call can only read; it cannot be marked as changing data.",
      base_url_required: "Enter the address of the system.",
      host_not_allowed: "The platform is not allowed to reach {{host}}. Ask the platform team to allow it.",
      url_invalid: "This is not a valid web address.",
      auth_incomplete: "Sign-in setting missing: {{field}}.",
      soap_port_required: "Choose which part of the service to use.",
      soap_port_choose: "The service has several connection points; we chose the first. You can change it in step 2.",
      database_required: "Enter the database connection.",
      mcp_url_required: "Enter the MCP server address.",
      mcp_image_not_approved: "The image {{image}} is not on the approved list.",
      mcp_stdio_launcher_unavailable: "Local MCP servers cannot be started yet on this platform.",
      egress_hosts_required: "Say which address the system is at.",
      operation_skipped: "{{operation}} was skipped: {{reason}}",
      schema_import_ignored: "An external schema was not loaded: {{location}}",
      ai_unavailable: "The local model is not available, so descriptions were not drafted.",
    },
    lang: { en: "English", ar: "Arabic" },
    json: "Response",
    untrusted: "Untrusted data",
  },
  ar: {
    entry: "ربط نظام",
    title: "ربط نظام",
    subtitle: "اسمح للوكلاء باستخدام أحد أنظمة شركتك خطوة بخطوة. لا يصل شيء إلى الوكلاء قبل أن تطلقه، وتبقى كل القواعد والضوابط الوقائية مطبقة.",
    back: "السابق",
    next: "التالي",
    restart: "البدء من جديد",
    steps: { type: "اختر النوع", source: "حدّد مكانه", review: "المراجعة", credentials: "الدخول", test: "التجربة", release: "الحفظ والإطلاق" },
    stepsLabel: "خطوات الدليل",
    type: {
      heading: "ما نوع النظام؟",
      rest: { title: "واجهة REST", body: "واجهة ويب لها وصف OpenAPI ‏(Swagger 3)." },
      soap: { title: "خدمة SOAP", body: "خدمة ويب أقدم يصفها ملف WSDL." },
      database: { title: "قاعدة بيانات", body: "قاعدة بيانات PostgreSQL. نقترح عمليات بحث آمنة للقراءة فقط." },
      mcp: { title: "خادم MCP", body: "خادم يقدّم أدوات جاهزة لمساعدي الذكاء الاصطناعي (بروتوكول MCP)." },
      other: "مراسلة أو بريد إلكتروني أو ملفات أو غير ذلك؟ هذه تستخدم موصلات جاهزة — راجع الدليل.",
      otherLink: "اقرأ الدليل",
      choose: "اختر {{type}}",
    },
    source: {
      heading: "أين هو؟",
      name: "اسم قصير لهذا النظام",
      nameHelp: "حروف إنجليزية صغيرة وأرقام وشرطات، مثل demo-erp. يراه الوكلاء والمصممون.",
      how: "كيف نقرأ وصفه؟",
      fromUrl: "من عنوان ويب",
      fromFile: "من ملف",
      url: "عنوان الوصف",
      urlHelpRest: "مثل https://erp.example/openapi.json",
      urlHelpSoap: "مثل https://registry.example/service?wsdl",
      file: "اختر ملفًا",
      paste: "أو الصق المحتوى",
      fileLoaded: "تم تحميل {{name}}",
      baseUrl: "عنوان النظام (اختياري)",
      baseUrlHelp: "حيث تُرسل الطلبات. اتركه فارغًا لاستخدام العنوان الموجود في الوصف.",
      port: "أي جزء من الخدمة؟",
      portHelp: "يقدّم الوصف عدة نقاط اتصال. اختر واحدة.",
      dbHost: "خادم قاعدة البيانات",
      dbPort: "المنفذ",
      dbName: "اسم قاعدة البيانات",
      dbSchemas: "المخططات المطلوب فحصها",
      dbSchemasHelp: "افصل بينها بفواصل. عادةً public.",
      dbUser: "اسم المستخدم (مستخدم للقراءة فقط)",
      dbPassword: "كلمة المرور",
      dbUserHelp: "استخدم مستخدمًا يقرأ فقط. يُستخدم الآن لفحص الجداول فقط؛ وتحفظ بيانات الدخول في الخطوة 4.",
      dbWrites: "اقترح أيضًا عمليات تغيّر البيانات",
      dbWritesWarn: "فقط إذا كان على الوكلاء إضافة صفوف أو تغييرها. لكل عملية تراجع تلقائي وتحتاج إلى موافقة.",
      mcpFrom: "أين الخادم؟",
      mcpUrl: "عنوان الخادم",
      mcpUrlHelp: "مثل https://mcp.example/mcp",
      mcpListing: "من سوق المنصة",
      mcpListingPick: "اختر خادم MCP من السوق",
      mcpListingNone: "لا توجد خوادم MCP في السوق.",
      mcpTransport: "نوع الاتصال",
      transport: { streamable_http: "HTTP قابل للبث (موصى به)", sse: "أحداث مرسلة من الخادم (خوادم أقدم)" },
      mcpToken: "رمز وصول لقراءة أدواته (اختياري)",
      mcpTokenHelp: "يُستخدم الآن فقط لعرض الأدوات. لا يُحفظ.",
      mcpLocal: "الخوادم التي تعمل على هذه المنصة (خوادم MCP محلية) تُضاف فقط من صور اعتمدها فريق المنصة.",
      mcpLocalLink: "كيف تُعتمد الخوادم المحلية",
      mcpLocalOption: "خادم محلي على هذه المنصة",
      mcpLocalPick: "اختر خادم MCP محليًا معتمدًا",
      mcpLocalNone: "لا يوجد خادم MCP محلي معتمد بعد. يعتمدها مسؤول المنصة في الإدارة › إعدادات المنصة.",
      mcpLocalInternet: "يمكنه استخدام الإنترنت (عبر قائمة المنصة المسموح بها)",
      mcpLocalHow: "يعمل في بيئة معزولة ومقفلة في كل مرة يستخدمه فيها وكيل: لا ملفات ولا أسرار ولا إنترنت إلا إذا سمح فريق المنصة بذلك.",
      ai: "صياغة أوصاف مبسطة بالنموذج المحلي",
      aiHelp: "اختياري. تُعلَّم المسودات ويمكنك تعديلها. يُتجاوز إذا لم يتوفر نموذج محلي.",
      discover: "افحصه",
      discovering: "جارٍ قراءة الوصف…",
    },
    discoverError: {
      title: "تعذّرت القراءة",
      source_missing: "أدخل عنوان ويب أو ملفًا.",
      fetch_not_allowed: "لا يُسمح للمنصة بالوصول إلى {{host}}. ارفع الملف بدلًا من ذلك، أو اطلب من فريق المنصة السماح بهذا العنوان.",
      fetch_failed: "تعذّر تنزيل الوصف. تحقق من العنوان.",
      too_large: "الملف كبير جدًا.",
      parse_failed: "لا يبدو هذا وصفًا نفهمه.",
      openapi_version_unsupported: "هذا وصف Swagger 2 قديم. حوّله إلى OpenAPI 3 ثم أعد المحاولة.",
      wsdl_invalid: "هذا ليس وصف WSDL 1.1.",
      wsdl_unsafe: "يحتوي الملف على XML غير آمن (DTD أو كيانات) فرُفض.",
      wsdl_no_ports: "لا يصف ملف WSDL أي خدمة SOAP يمكن استدعاؤها.",
      database_unreachable: "تعذّر الوصول إلى قاعدة البيانات. تحقق من اسم الخادم والمنفذ.",
      database_auth_failed: "رفضت قاعدة البيانات اسم المستخدم أو كلمة المرور.",
      mcp_unreachable: "تعذّر الوصول إلى خادم MCP.",
      mcp_stdio_not_supported: "خوادم MCP المحلية تُضاف فقط من صور معتمدة.",
    },
    review: {
      heading: "ما وجدناه",
      intro: "هذه هي العمليات التي يمكن للوكلاء استخدامها. راجع كل واحدة. الاقتراحات مجرد اقتراحات: نوضح سبب كل منها.",
      systemName: "الاسم",
      version: "الإصدار",
      descEn: "الوصف (بالإنجليزية)",
      descAr: "الوصف (بالعربية)",
      read: "قراءة فقط",
      readHelp: "هذه تطّلع على البيانات فقط.",
      change: "تغيّر البيانات",
      changeHelp: "هذه تضيف بيانات أو تغيّرها أو تحذفها. كل واحدة تحتاج إلى طريقة للتراجع عنها أو سبب يوضح لماذا لا يمكن التراجع.",
      none: "لا شيء في هذه المجموعة.",
      include: "تضمين {{name}}",
      included: "مضمّنة",
      excluded: "مستبعدة",
      actionName: "اسم العملية",
      aiDrafted: "صاغها النموذج المحلي",
      kind: "ماذا تفعل",
      kindRead: "تقرأ فقط",
      kindChange: "تغيّر البيانات",
      kindLocked: "هذا النوع من الطلبات يقرأ فقط.",
      suggested: "مقترح: {{value}}",
      because: "لأن {{reason}}",
      apply: "طبّق الاقتراح",
      applyAll: "طبّق كل الاقتراحات",
      undo: "كيف يُتراجع عنها",
      undoNone: "لا يمكن التراجع عنها",
      undoAction: "التراجع عبر: {{name}}",
      undoBuiltin: "تراجع مدمج: {{text}}",
      undoArgs: "من أين يأخذ التراجع قيمه",
      undoArg: "{{param}} يأتي من",
      undoArgHelp: "args.<الاسم> من الطلب الأصلي أو result.<المسار> من نتيجته",
      reason: "لماذا لا يمكن التراجع عنها",
      reasonHelp: "مثال: يتجاهل النظام الطلبات المكررة التي تحمل المرجع نفسه.",
      pii: "بيانات شخصية في النتيجة",
      piiHelp: "تُخفى عمّن لا يحق له رؤية البيانات الشخصية.",
      piiBadge: "بيانات شخصية",
      approvers: "من يعتمدها",
      approversHelp: "يعتمد أصحاب أحد هذه الأدوار كل تغيير قبل حدوثه.",
      alwaysApprove: "اطلب الموافقة دائمًا",
      fieldsNone: "لا حقول",
      count: "{{included}} من {{total}} عمليات مضمّنة",
      problems: "أصلح هذه قبل المتابعة",
      warnings: "تستحق المراجعة",
      check: "تحقّق",
    },
    credentials: {
      heading: "كيف تسجّل المنصة الدخول",
      intro: "تُحفظ بيانات الدخول في مخزن الأسرار في المنصة. لا يمكن لأحد رؤيتها هنا مرة أخرى؛ يمكنك استبدالها فقط.",
      kind: "طريقة الدخول",
      kinds: {
        none: "بدون تسجيل دخول",
        api_key: "مفتاح API",
        bearer: "رمز وصول",
        oauth2_client_credentials: "بيانات عميل OAuth 2",
        basic: "اسم مستخدم وكلمة مرور",
        wsse: "اسم مستخدم وكلمة مرور WS-Security",
        database: "مستخدم قاعدة البيانات وكلمة المرور",
      },
      suggestedKind: "مقترح: {{kind}} — {{reason}}",
      location: "أرسل المفتاح في",
      header: "ترويسة",
      query: "العنوان (معامل استعلام)",
      param: "اسم الترويسة أو المعامل",
      tokenUrl: "عنوان الحصول على الرمز",
      scope: "النطاق (اختياري)",
      fields: {
        api_key: "مفتاح API",
        token: "رمز الوصول",
        client_id: "معرّف العميل",
        client_secret: "سر العميل",
        username: "اسم المستخدم",
        password: "كلمة المرور",
        soap_username: "اسم المستخدم",
        soap_password: "كلمة المرور",
      },
      sameAsDb: "استخدم مستخدم الخطوة 2",
      stored: "محفوظة ✓",
      usingStored: "يُستخدم تسجيل الدخول المحفوظ ({{kind}}) لهذا النظام. اختر طريقة أخرى في الأعلى، أو استبدل البيانات أدناه.",
      storedAt: "آخر تحديث {{when}} بواسطة {{who}}",
      notStored: "لم تُحفظ بعد",
      replace: "استبدال",
      save: "احفظ بيانات الدخول",
      saved: "حُفظت بيانات الدخول",
      noneNeeded: "لا يحتاج هذا النظام إلى تسجيل دخول.",
      missing: "أكمل: {{fields}}",
    },
    test: {
      heading: "جرّبه",
      intro: "عمليات القراءة تُنفَّذ فعلًا. العمليات التي تغيّر البيانات تعرض فقط ما سيُرسل بالضبط — دون تغيير أي شيء. تُسجَّل كل تجربة في سجل التدقيق.",
      action: "العملية المراد تجربتها",
      args: "القيم (JSON)",
      argsInvalid: "القيم غير صالحة: {{errors}}",
      run: "شغّل التجربة",
      dryRun: "تجربة دون تنفيذ",
      dryRunNote: "لم يتغير شيء. هذا ما سيُرسل بالضبط؛ تظهر الأسرار على شكل ***.",
      executedNote: "هذا ما أجاب به النظام. تعامل معه كبيانات وليس كتعليمات.",
      redacted: "بيانات شخصية مخفية: {{fields}}",
      audit: "سجل التدقيق {{id}}",
      failed: "لم تنجح التجربة",
      errors: {
        secret_missing: "لم تُحفظ بيانات الدخول بعد. احفظها في الخطوة السابقة.",
        downstream_error: "لم يُجب النظام. حاول لاحقًا.",
        downstream_rejected: "رفض النظام الطلب.",
        policy_denied: "القواعد لا تسمح بهذه التجربة.",
        timeout: "تأخر النظام في الإجابة.",
      },
      optional: "التجربة اختيارية لكنها موصى بها.",
    },
    release: {
      heading: "الحفظ والإطلاق",
      intro: "نسجّل هذا الإصدار، ونطلقه في مرحلة التطوير، وننتظر حتى يتمكن الوكلاء من استخدامه.",
      version: "الإصدار",
      versionExists: "الإصدار {{version}} مسجّل مسبقًا. هل تستخدم {{suggested}}؟",
      useSuggested: "استخدم {{version}}",
      diffTitle: "ما الذي يتغير مقارنةً بالإصدار {{version}}",
      added: "جديد: {{names}}",
      removed: "محذوف: {{names}}",
      changes: {
        kind_to_change: "أصبحت تغيّر البيانات",
        kind_to_read: "أصبحت تقرأ فقط",
        undo: "تغيّرت طريقة التراجع",
        pii: "تغيّرت البيانات الشخصية",
        approvers: "تغيّر المعتمدون",
        inputs: "تغيّرت القيم التي تأخذها",
        outputs: "تغيّر ما تعيده",
        description: "تغيّر الوصف",
      },
      changed: "{{name}}: {{changes}}",
      go: "احفظ وأطلق",
      progress: {
        build: "التحقق والتجهيز",
        register: "تسجيل الإصدار {{version}}",
        promote: "الإطلاق في مرحلة التطوير",
        reload: "التحميل في مضيف الموصلات",
        policy: "الانتظار حتى تسمح القواعد باستخدامه",
      },
      waiting: "ما زلنا ننتظر تحديث القواعد. يستغرق عادةً أقل من دقيقة.",
      retry: "تحقّق مجددًا",
      failedStep: "فشلت هذه الخطوة",
      done: "{{name}} {{version}} جاهز",
      doneBody: "يمكن للمصممين الآن إضافته إلى الوكلاء. وما زال الوكلاء بحاجة إلى موافقة على أي تغيير في البيانات.",
      useInBuilder: "استخدمه في المصمم",
      another: "اربط نظامًا آخر",
      alreadyDeployed: "أُطلق هذا الإصدار مسبقًا.",
    },
    problems: {
      name_invalid: "استخدم من 2 إلى 63 حرفًا إنجليزيًا صغيرًا أو رقمًا أو شرطة، يبدأ بحرف.",
      version_invalid: "يجب أن يكون الإصدار مثل 1.0.0.",
      description_missing: "أضف وصفًا باللغة {{lang}}.",
      no_actions: "ضمّن عملية واحدة على الأقل.",
      action_name_invalid: "أسماء العمليات حروف إنجليزية وأرقام وشرطات سفلية وتبدأ بحرف.",
      action_name_duplicate: "لعمليتين الاسم نفسه.",
      undo_or_reason_required: "هذه العملية تغيّر البيانات. اختر طريقة للتراجع عنها، أو وضّح لماذا لا يمكن التراجع عنها.",
      undo_target_missing: "عملية التراجع {{target}} غير موجودة.",
      undo_target_self: "لا يمكن لعملية أن تتراجع عن نفسها.",
      undo_args_missing: "يحتاج التراجع إلى قيم لـ {{params}}. وضّح من أين تأتي كل قيمة.",
      read_cannot_change: "هذا النوع من الطلبات يقرأ فقط ولا يمكن وصفه بأنه يغيّر البيانات.",
      base_url_required: "أدخل عنوان النظام.",
      host_not_allowed: "لا يُسمح للمنصة بالوصول إلى {{host}}. اطلب من فريق المنصة السماح به.",
      url_invalid: "هذا ليس عنوان ويب صالحًا.",
      auth_incomplete: "إعداد دخول ناقص: {{field}}.",
      soap_port_required: "اختر جزء الخدمة المراد استخدامه.",
      soap_port_choose: "للخدمة عدة نقاط اتصال؛ اخترنا الأولى. يمكنك تغييرها في الخطوة 2.",
      database_required: "أدخل بيانات الاتصال بقاعدة البيانات.",
      mcp_url_required: "أدخل عنوان خادم MCP.",
      mcp_image_not_approved: "الصورة {{image}} ليست في القائمة المعتمدة.",
      mcp_stdio_launcher_unavailable: "لا يمكن تشغيل خوادم MCP المحلية على هذه المنصة بعد.",
      egress_hosts_required: "حدّد عنوان النظام.",
      operation_skipped: "تم تجاوز {{operation}}: {{reason}}",
      schema_import_ignored: "لم يُحمَّل مخطط خارجي: {{location}}",
      ai_unavailable: "النموذج المحلي غير متاح، لذا لم تُصغ الأوصاف.",
    },
    lang: { en: "الإنجليزية", ar: "العربية" },
    json: "الاستجابة",
    untrusted: "بيانات غير موثوقة",
  },
};
